import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { PermissionsProvider } from './contexts/PermissionsContext';
import Layout from './components/Layout';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Companies from './pages/Companies';
import CompanyDetail from './pages/CompanyDetail';
import Contacts from './pages/Contacts';
import ContactDetail from './pages/ContactDetail';
import Pipeline from './pages/Pipeline';
import CalendarPage from './pages/Calendar';
import Activities from './pages/Activities';
import Orders from './pages/Orders';
import OrderDetail from './pages/OrderDetail';
import Invoices from './pages/Invoices';
import Analytics from './pages/Analytics';
import Reports from './pages/Reports';
import RolesPage from './pages/RolesPage';
import Settings from './pages/Settings';
import SuperAdmin from './pages/SuperAdmin';
import Products from './pages/Products';
import Invitations from './pages/Invitations';
import ForecastPage from './pages/ForecastPage';
import InventoryPage from './pages/InventoryPage';
import ProductionPage from './pages/ProductionPage';
import PackagingCatalog from './pages/PackagingCatalog';
import RaccoltaPage from './pages/RaccoltaPage';
import ProductStockPage from './pages/ProductStockPage';
import ModuleGate from './components/ModuleGate';
import MandatoryPasswordChange from './components/MandatoryPasswordChange';
import './App.css';

function ProtectedRoute({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-3">
        <div className="w-10 h-10 rounded-full border-4 border-primary border-t-transparent animate-spin" />
        <p className="text-muted-foreground text-sm">Caricamento...</p>
      </div>
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRoutes() {
  const { user } = useAuth();
  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/dashboard" replace /> : <Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="/" element={
        <ProtectedRoute>
          <Layout />
        </ProtectedRoute>
      }>
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="companies" element={<Companies />} />
        <Route path="companies/:id" element={<CompanyDetail />} />
        <Route path="contacts" element={<Contacts />} />
        <Route path="contacts/:id" element={<ContactDetail />} />
        <Route path="pipeline" element={<ModuleGate module="pipeline"><Pipeline /></ModuleGate>} />
        <Route path="calendar" element={<ModuleGate module="calendar"><CalendarPage /></ModuleGate>} />
        <Route path="activities" element={<ModuleGate module="activities"><Activities /></ModuleGate>} />
        <Route path="orders" element={<ModuleGate module="orders"><Orders /></ModuleGate>} />
        <Route path="orders/:id" element={<ModuleGate module="orders"><OrderDetail /></ModuleGate>} />
        <Route path="products" element={<Products />} />
        <Route path="invoices" element={<ModuleGate module="orders"><Invoices /></ModuleGate>} />
        <Route path="analytics" element={<ModuleGate module="analytics"><Analytics /></ModuleGate>} />
        <Route path="reports" element={<ModuleGate module="reports"><Reports /></ModuleGate>} />
        <Route path="forecast" element={<ForecastPage />} />
        <Route path="scorte" element={<ModuleGate module="inventory"><InventoryPage /></ModuleGate>} />
        <Route path="produzione" element={<ModuleGate module="production"><ProductionPage /></ModuleGate>} />
        <Route path="packaging" element={<ModuleGate module="orders"><PackagingCatalog /></ModuleGate>} />
        <Route path="raccolta" element={<ModuleGate module="orders"><RaccoltaPage /></ModuleGate>} />
        <Route path="product-stock" element={<ModuleGate module="orders"><ProductStockPage /></ModuleGate>} />
        <Route path="roles" element={
          <ProtectedRoute roles={['admin', 'super_admin']}>
            <RolesPage />
          </ProtectedRoute>
        } />
        <Route path="settings" element={
          <ProtectedRoute roles={['admin', 'super_admin']}>
            <Settings />
          </ProtectedRoute>
        } />
        <Route path="invitations" element={
          <ProtectedRoute roles={['admin', 'super_admin']}>
            <Invitations />
          </ProtectedRoute>
        } />
        <Route path="superadmin" element={
          <ProtectedRoute roles={['super_admin']}>
            <SuperAdmin />
          </ProtectedRoute>
        } />
      </Route>
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <PermissionsProvider>
          <MandatoryPasswordChange />
          <AppRoutes />
        </PermissionsProvider>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
